import { ChevronRightIcon } from "@heroicons/react/solid";
import moment from "moment-timezone";
import { Moment } from "moment-timezone";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { ApiCommonParams } from "../../../api/@types/apiTypes";
import { createStudentSchedule } from "../../../api/endpoints/liaisons/schedules";
import { indexLiaisonsStudents } from "../../../api/endpoints/liaisons/students";
import {
  indexLiaisonTutors,
  showLiaisonTutor,
} from "../../../api/endpoints/liaisons/tutors";
import { SlotStates } from "../../../api/models/ScheduleSlotModel";
import StudentModel from "../../../api/models/StudentModel";
import TutorModel from "../../../api/models/TutorModel";
import UserModel from "../../../api/models/UserModel";
import { ToastContext } from "../../../App";
import FiltersBar from "../../../components/app-filters/FiltersBar";
import UserProfilePictureAvatar from "../../../components/avatar/UserProfilePictureAvatar";
import ScheduleCalendar from "../../../components/calendar/ScheduleCalendar";
import DropDown from "../../../components/forms/dropdown/DropDown";
import LoadingSpinner from "../../../components/LoadingSpinner";
import ReviewScheduleModal from "../../../components/modal/views/ReviewScheduleModal";
import useCalendarViewDate from "../../../hooks/useCalendarViewDate";
import { useQueryParams } from "../../../hooks/useQueryParams";
import CalendarScheduleModel from "../../../models/CalendarScheduleModel";
import SchedulingService from "../../../services/schedulingService";

export default function ScheduleView() {
  const LocalBasePath = "page.liaison.dashboard.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const navigate = useNavigate();
  const query = useQueryParams();
  const toast = useContext(ToastContext);
  const [schedule, setSchedule] = useState<CalendarScheduleModel | null>(null);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [users, setUsers] = useState<{ [key: string]: any }>({});
  let [currentPageFilter, setCurrentPageFilter] = useState<{
    [key: string]: any;
  }>({
    currentStudentPage: 1,
    currentTutorPage: 1,
  });
  // The following is a workaround to force a functional component to re-render
  const [, updateState] = useState<Object>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const {
    data: tutors,
    isLoading: isLoadingTutors,
    fetchNextPage: fetchNextPageTutors,
  } = useInfiniteQuery(
    ["liaisonTutors", users.student?.tutorGender, users.student?.telawah_id],
    ({ queryKey, pageParam = 1 }) => {
      let apiParams: ApiCommonParams = {
        filters: {
          gender: queryKey[1],
          telawah_id: queryKey[2],
        },
        pagination: { pageSize: 25, pageNumber: pageParam },
      };
      return indexLiaisonTutors(apiParams);
    },
    {
      staleTime: 5 * 60 * 1000,
      select: (data) => {
        let tutorList: TutorModel[] = [];
        return {
          pages: data.pages
            .map((pageResponse) => {
              if (pageResponse.isSuccess) {
                tutorList = TutorModel.fromApiResponse(pageResponse.data);
              }
              return tutorList;
            })
            .flat(),
          pageParams: data.pageParams,
        };
      },
      cacheTime: 0,
    }
  );

  const {
    data: students,
    isLoading: isLoadingStudents,
    fetchNextPage: fetchNextPageStudents,
  } = useInfiniteQuery(
    "liaisonStudentsExtended",
    ({ pageParam = 1 }) => {
      let apiParams: ApiCommonParams = {
        filters: { is_scheduled: false },
        viewName: "extended",
        pagination: { pageSize: 25, pageNumber: pageParam },
      };

      return indexLiaisonsStudents(apiParams);
    },
    {
      staleTime: 0,
      select: (data) => {
        let studentList: StudentModel[] = [];
        return {
          pages: data.pages
            .map((pageResponse) => {
              if (pageResponse.isSuccess) {
                studentList = StudentModel.fromApiResponse(pageResponse.data);
              }
              return studentList;
            })
            .flat(),
          pageParams: data.pageParams,
        };
      },
      cacheTime: 0,
    }
  );

  const setCalendarSchedule = (availabilitySchedule: object) => {
    setSchedule(
      new CalendarScheduleModel(availabilitySchedule, SlotStates.reservable)
    );
  };

  const { isLoading: isLoadingSchedule } = useQuery(
    ["availabilitySchedule", users.tutor?.id],
    ({ queryKey }) => showLiaisonTutor(queryKey[1]!),
    {
      staleTime: Infinity,
      cacheTime: 0,
      onSuccess: (data) => {
        if (!!data?.availabilitySchedule) {
          setCalendarSchedule(data.availabilitySchedule);
          return data;
        }
        setSchedule(null);
      },
    }
  );
  const studentsList = students?.pages || [];

  // @description: Get the student id from the url and setting it in the state
  useEffect(() => {
    const studentId = query.get("student_id");
    const selectedStudent = getUser(studentsList!, studentId!);
    if (selectedStudent?.id !== users.student?.id) {
      setUsers((u) => ({ ...u, student: selectedStudent }));
    }
  }, [query.get("student_id"), students]);
  useEffect(() => {
    fetchNextPageTutors({
      pageParam: currentPageFilter.currentTutorPage,
    });
  }, [currentPageFilter.currentTutorPage]);
  useEffect(() => {
    fetchNextPageStudents({
      pageParam: currentPageFilter.currentStudentPage,
    });
  }, [currentPageFilter.currentStudentPage]);
  const onSelectStudent = (value: string) => {
    navigate(`/liaison/dashboard/schedule?student_id=${value}`, {
      replace: true,
    });
  };

  const getUserOptions = (users: UserModel[]) => {
    return (
      users?.map((user) => ({
        key: user.fullName!,
        value: `${user.id}`,
      })) || []
    );
  };

  const getUser = (users: UserModel[], id: string) => {
    return users?.find((user) => `${user.id}` === id) || null;
  };
  const tutorsList = tutors?.pages.map((tutor: TutorModel) => tutor) || [];

  const handleSetUser = (type: string, val: any) => {
    if (type === "student") {
      setUsers((u) => ({ ...u, student: getUser(studentsList!, val) }));
      onSelectStudent(val);
    } else {
      setUsers((u) => ({ ...u, tutor: getUser(tutorsList!, val) }));
    }
  };

  const getUserId = (type: string) => {
    return users[type]?.id && `${users[type].id}`;
  };

  const onSlotClick = (slot: any) => {
    let schedulingService = new SchedulingService(schedule!, users?.student!);
    schedulingService.toggleSlot(slot);
    setSchedule(schedulingService.schedule);
    forceUpdate();
  };

  const onCreateSchedule = async () => {
    let schedulingService = new SchedulingService(schedule!, users?.student!);
    if (schedulingService.hasScheduledSlots()) {
      const response = await createStudentSchedule(
        schedulingService.student.id,
        schedulingService.generateApiSchedule(users?.tutor?.id!)
      );
      if (response.isSuccess) {
        toast?.success("scheduleCreatedSuccessfully");
        navigate(`/liaison/dashboard`);
      } else {
        toast?.error("unableToCreateSchedule");
      }
    }
  };

  const onReviewSchedule = () => {
    if (!!schedule && schedule.hasScheduledSlots) {
      setShowReviewModal(true);
    } else {
      toast?.error("unableToCreateSchedule");
    }
  };

  const Spinner = () => {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <LoadingSpinner size={40} />
      </div>
    );
  };

  if (isLoadingStudents) return <Spinner />;

  return (
    <div className="flex flex-col w-full divide-y">
      <div className="pb-6 bg-gray-50">
        <div className="flex flex-col gap-2 p-4 text-gray-900 divide-gray-300 sm:flex-row sm:items-center">
          <h2 className="text-2xl font-semibold">{t("scheduleReaders")}</h2>
          <span className="text-sm font-medium text-gray-400">
            {users?.student?.sponsorName}
          </span>
        </div>

        <div className="flex flex-col gap-4 mx-4 sm:flex-row">
          <FiltersBar
            onLoadMore={(key: string) =>
              setCurrentPageFilter({
                ...currentPageFilter,
                [key]: ++currentPageFilter[key],
              })
            }
            fields={[
              {
                name: "student",
                label: t("reader"),
                options: getUserOptions(studentsList!),
                paginationLabel: "currentStudentPage",
              },
              {
                name: "tutor",
                label: t("reciter"),
                options: getUserOptions(tutorsList!),
                paginationLabel: "currentTutorPage",
              },
            ]}
            setFilters={handleSetUser}
            getFilterValue={getUserId}
            cypressSelector={"scheduleStudentFilter"}
          />
          {!!users?.tutor && !!users?.student && (
            <div className="flex items-end justify-center whitespace-nowrap">
              <button
                type="button"
                onClick={() => setUsers((u) => ({ ...u, tutor: null }))}
                className="inline-flex items-center px-4 py-2 text-base font-medium text-gray-700 rounded-md shadow-sm bg-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-100"
              >
                {t("showAllReciters")}
              </button>
            </div>
          )}
        </div>
        {!!users?.tutor && !!users?.student && (
          <div className="mx-4 mt-5">
            <p className="text-sm font-medium text-gray-400">
              <span className="me-2">{t("timeRangeParagraph")}</span>
            </p>
          </div>
        )}
      </div>

      {!!users?.tutor && !!users?.student ? (
        <>
          {!!isLoadingSchedule ? (
            <Spinner />
          ) : (
            <>
              <ScheduleCalendar
                selectedDate={viewingDate}
                viewingDate={viewingDate}
                schedule={schedule}
                onChangeViewingDate={onChangeViewingDate}
                onSlotClick={onSlotClick}
                compact={true}
                showDates={false}
              />
              <div className="flex justify-end flex-shrink-0 px-4 py-4">
                <button
                  type="button"
                  data-cy="cancel"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
                  onClick={() => setUsers((u) => ({ ...u, tutor: null }))}
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  data-cy="submit"
                  className="inline-flex justify-center px-4 py-2 mx-4 text-sm font-medium text-white border border-transparent rounded-md shadow-sm bg-brandblue-600 hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2 disabled:bg-gray-400"
                  onClick={onReviewSchedule}
                  disabled={!(!!schedule && schedule.hasScheduledSlots)}
                >
                  {t("scheduleReader")}
                </button>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="w-full pt-16 text-center">
          <div className="px-4">
            <h3 className="text-lg font-medium text-gray-700">
              {t("chooseTutorHeader")}
            </h3>
            <p className="mt-1 text-base font-normal text-gray-500">
              {t("chooseTutorParagraph")}
            </p>
          </div>
          {isLoadingTutors ? (
            <Spinner />
          ) : (
            <div className="flex justify-center">
              <div className="w-full max-w-full mt-6 md:w-96">
                <ul role="list" className="divide-y divide-gray-200">
                  {tutorsList?.map((tutor, idx) => (
                    <li
                      key={`Tutor-${idx}-${tutor.email}`}
                      className="block hover:bg-gray-50"
                      data-cy="tutor"
                      role={"button"}
                      onClick={() => {
                        setUsers((u) => ({ ...u, tutor }));
                      }}
                    >
                      <div className="flex items-center py-4 ps-4 sm:ps-6">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="flex-shrink-0">
                            <UserProfilePictureAvatar
                              profilePictureUrl={tutor?.profile_picture_url}
                              className="w-10 h-10"
                            />
                          </div>
                          <div className="flex-1 min-w-0 px-4">
                            <div>
                              <p className="text-sm font-medium truncate text-brandblue-600 ltr:text-left rtl:text-right">
                                {tutor.full_name}
                              </p>
                              <p className="flex items-center mt-2 text-sm text-gray-500">
                                <span
                                  className="truncate"
                                  title={t("occupancyRate")}
                                >
                                  {`${tutor.occupancyRate}%`}
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="p-4">
                          <ChevronRightIcon
                            className="w-5 h-5 text-gray-400 rtl:rotate-180"
                            aria-hidden="true"
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
      <ReviewScheduleModal
        setOpen={setShowReviewModal}
        open={showReviewModal}
        schedule={schedule!}
        onSubmit={onCreateSchedule}
      />
    </div>
  );
}
